import { useQuery } from "@tanstack/react-query";
import { useAtomValue } from "jotai";
import { useDataProvidersApi, useGeoDataApi } from "../../../hooks";
import { analyticsFiltersAtom } from "../state";

/**
 * Custom hook that formats active analytics filters into human-readable text
 * for display in exported images.
 *
 * Uses the same React Query keys as filter components to deduplicate API requests.
 * Returns empty string if no filters are active or while data is loading.
 */
export const useFilterText = (): string => {
    const filters = useAtomValue(analyticsFiltersAtom);
    const dataProvidersApi = useDataProvidersApi();
    const geoDataApi = useGeoDataApi();

    // Fetch provider data
    const dataProvidersQuery = useQuery({
        queryKey: ["analyticsDataProviders"],
        queryFn: async () => {
            return await dataProvidersApi.dataProvidersList();
        },
        refetchOnWindowFocus: false,
        staleTime: Infinity,
    });

    // Fetch location preset data
    const geoFilterItemsQuery = useQuery({
        queryKey: ["geoFilterItems"],
        queryFn: async () => {
            const response = await geoDataApi.geodataItemsList({
                pageSize: 1000,
            });
            return response.results;
        },
        staleTime: Infinity,
    });

    // Return empty string while loading
    if (dataProvidersQuery.isLoading || geoFilterItemsQuery.isLoading) {
        return "";
    }

    const parts: string[] = [];

    // Format providers (if filtered)
    if (
        filters.providers &&
        filters.providers !== "all" &&
        Array.isArray(filters.providers) &&
        filters.providers.length > 0
    ) {
        const dataProviders = dataProvidersQuery.data?.results || [];
        const providerTexts = filters.providers
            .map((p) => {
                const provider = dataProviders.find((dp) => dp.id === p.id);
                if (!provider) return undefined;

                let text = provider.name;

                // Add secondary sources in parentheses if specified
                if (p.se && p.se.length > 0) {
                    // Check if all secondary sources are selected
                    if (p.se.length === provider.secondaryDataSources?.length) {
                        text += " (all)";
                    } else {
                        // Show the specific secondary sources
                        text += ` (${p.se.join(", ")})`;
                    }
                }

                return text;
            })
            .filter((name): name is string => name !== undefined);

        if (providerTexts.length > 0) {
            const label = providerTexts.length === 1 ? "Provider" : "Providers";
            parts.push(`${label}: ${providerTexts.join(", ")}`);
        }
    }

    // Format location presets (if filtered)
    if (filters.locationPreset && filters.locationPreset.length > 0) {
        const items = geoFilterItemsQuery.data || [];
        const locationsSelected = items.filter((i) =>
            filters.locationPreset.includes(i.id),
        );
        const locationNames = locationsSelected.map((l) => l.name);

        if (locationNames.length > 0) {
            const label = locationNames.length === 1 ? "Location" : "Locations";
            parts.push(`${label}: ${locationNames.join(", ")}`);
        }
    }

    // Format site types (if filtered)
    if (filters.siteType && filters.siteType.length > 0) {
        const siteTypeNames = filters.siteType.map((siteType) => {
            // Handle "null" as "Other"
            if (siteType === "null") {
                return "Other";
            }
            // Format snake_case to Title Case
            return siteType
                .replaceAll("_", " ")
                .replace(/\b\w/g, (char) => char.toUpperCase());
        });

        const label = siteTypeNames.length === 1 ? "Site Type" : "Site Types";
        parts.push(`${label}: ${siteTypeNames.join(", ")}`);
    }

    // Join all parts with ". " separator
    const result = parts.join(". ");
    return result;
};
